import { Link } from 'react-router-dom';
import { FaTimes, FaUndo, FaPlus } from 'react-icons/fa';
import { useQuickLinks } from '../context/UserPreferencesContext';
import { DEFAULT_QUICK_LINKS } from '../config/navigation';

const sameAsDefaults = (paths) =>
  paths.length === DEFAULT_QUICK_LINKS.length && paths.every((p, i) => p === DEFAULT_QUICK_LINKS[i]);

/**
 * The dashboard's quick links: the admin's own shortlist of pages, added from the sidebar and
 * removed here. Reset restores the defaults, which is also the only way back to a link that has no
 * sidebar row of its own (New Job).
 */
const QuickLinks = () => {
  const { paths, links, ready, error, remove, reset, dismissError } = useQuickLinks();

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6" role="region" aria-labelledby="quick-links-heading">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h2 id="quick-links-heading" className="text-lg font-semibold text-gray-800">Quick links</h2>
        {ready && !sameAsDefaults(paths) && (
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center text-sm text-gray-500 hover:text-jims-cyan-dark focus-visible:ring-2 focus-visible:ring-jims-cyan rounded px-2 py-1"
          >
            <FaUndo className="mr-2 text-xs" aria-hidden="true" />Reset to defaults
          </button>
        )}
      </div>

      {error && (
        <div role="alert" className="flex items-start justify-between gap-3 bg-red-50 border-l-4 border-red-400 text-red-700 text-sm p-3 mb-4 rounded">
          <span>{error}</span>
          <button type="button" onClick={dismissError} aria-label="Dismiss" className="shrink-0 p-1 hover:text-red-900">
            <FaTimes />
          </button>
        </div>
      )}

      {/* The defaults are only a placeholder until the saved list arrives — showing them first
          would flash four links at an admin who deliberately removed them. */}
      {!ready ? (
        <div className="h-10 w-full max-w-md rounded bg-gray-100 animate-pulse" aria-hidden="true" />
      ) : links.length === 0 ? (
        <p className="text-sm text-gray-500">
          No quick links yet. Add one with the <FaPlus className="inline mx-1 text-xs" aria-hidden="true" />
          button beside any menu item in the sidebar.
        </p>
      ) : (
        <div className="flex flex-wrap gap-3">
          {links.map((item) => (
            <div
              key={item.path}
              className={`inline-flex items-center rounded-md overflow-hidden text-sm font-medium ${
                item.accent ? 'bg-jims-cyan text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              <Link
                to={item.path}
                className={`inline-flex items-center px-4 py-2 ${item.accent ? 'hover:bg-jims-cyan-dark' : 'hover:bg-gray-200'}`}
              >
                <item.icon className="mr-2" aria-hidden="true" />{item.name}
              </Link>
              <button
                type="button"
                onClick={() => remove(item.path)}
                aria-label={`Remove ${item.name} from quick links`}
                title={`Remove ${item.name} from quick links`}
                className={`h-full px-2 py-2.5 focus-visible:ring-2 focus-visible:ring-inset ${
                  item.accent
                    ? 'text-cyan-100 hover:bg-jims-cyan-dark hover:text-white focus-visible:ring-white'
                    : 'text-gray-400 hover:bg-gray-200 hover:text-gray-700 focus-visible:ring-jims-cyan'
                }`}
              >
                <FaTimes className="text-xs" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuickLinks;
